import { Text2, skinVars } from '@telefonica/mistica';
import { AxiosError, AxiosResponse } from 'axios';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EProblemFeedback, EProblemFeedbackPhase, EProblemFeedbackType } from '../../models/Problems.interfaces';
import { literalsFeedback, LoaderMessage } from '../../pages/support/utils.chat';
import { getProblemById, validateProblemById } from '../../services/ProblemService';
import navigateParams from '../../utils/navigation/navigateParams';
import { resetResponses } from '../../utils/store/features/chat';
import ChatSupport from '../ChatSupport';
import ChatText from '../ChatText';

const ChatDiagnosisFinished: React.FC<{ problemId: string }> = ({ problemId }: { problemId: string }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [response, setResponse] = useState<string>();
    const responses: { text: string; value: boolean }[] = [
        {
            text: t('yes'),
            value: true,
        },
        {
            text: t('no'),
            value: false,
        },
    ];

    useEffect(() => {
        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
    }, [loading, dispatch]);

    const handleResponse = (conformity: boolean) => {
        setLoading(true);
        dispatch(resetResponses());
        setResponse(conformity ? responses[0].text : responses[1].text);
        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
        const isConformity = conformity ? 'conformity' : 'notConformity';
        const queryParams: { [key: string]: any } = {
            conformity: {
                type: EProblemFeedbackType.SUCCESS,
                ...literalsFeedback(EProblemFeedback.SUCCESS),
            },
            notConformity: {
                type: EProblemFeedbackType.INFO,
                phase: EProblemFeedbackPhase.REV,
                ...literalsFeedback(EProblemFeedback.REVISION_NOT_CONFORMITY),
            },
        };
        if (conformity) {
            setTimeout(() => {
                validateProblemById({ conformity, problemId })
                    .then(() => {
                        getProblemById({ problemId })
                            .then((response: AxiosResponse) => {
                                const { data } = response;
                                setLoading(false);

                                if (
                                    navigateParams(
                                        conformity ? 'feedback-success' : 'feedback-info',
                                        '/support/incidents-feedback',
                                        queryParams[isConformity],
                                        {},
                                        '',
                                    )
                                ) {
                                    navigate(
                                        `/support/incidents-feedback?type=${queryParams[isConformity].type}${
                                            !conformity ? `&phase=${queryParams.notConformity.phase}` : ''
                                        }&code=${params.get('code')}`,
                                    );
                                }
                            })
                            .catch((error: AxiosError) => {
                                console.error(error);
                                setLoading(false);
                                const queryParams = {
                                    phase: EProblemFeedbackType.ERROR,
                                    error: EProblemFeedback.DIAGNOSIS,
                                    ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                                };
                                if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                                    navigate(
                                        `/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`,
                                    );
                                }
                            });
                    })
                    .catch((error: AxiosError) => {
                        setLoading(false);
                        console.error(error);
                        const queryParams = {
                            phase: EProblemFeedbackType.ERROR,
                            error: EProblemFeedback.DIAGNOSIS,
                            ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                        };
                        if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                            navigate(
                                `/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`,
                            );
                        }
                    });
            }, 5000);
        } else {
            if (
                navigateParams(
                    conformity ? 'feedback-success' : 'feedback-info',
                    '/support/incidents-feedback',
                    queryParams[isConformity],
                    {},
                    '',
                )
            ) {
                navigate(
                    `/support/incidents-feedback?type=${queryParams[isConformity].type}${
                        !conformity ? `&phase=${queryParams.notConformity.phase}` : ''
                    }&code=${params.get('code')}`,
                );
            }
        }
    };
    return (
        <>
            <ChatSupport text={t('supportIssueResolve')}></ChatSupport>
            {response ? (
                <div className='chat-diagnosis__chatResponse'>
                    <ChatText context='response' text={response!}></ChatText>
                </div>
            ) : (
                responses.map((response: { text: string; value: boolean }, key: number) => {
                    const { text, value } = response;
                    return (
                        <div
                            key={key}
                            onClick={() => handleResponse(value)}
                            className='chat-response'
                            style={{
                                borderStyle: 'solid',
                                borderWidth: '1px',
                                borderColor: skinVars.colors.controlActivated,
                            }}
                        >
                            <Text2 regular color={skinVars.colors.textLink}>
                                {text}
                            </Text2>
                        </div>
                    );
                })
            )}
            {loading && LoaderMessage}
        </>
    );
};

export default ChatDiagnosisFinished;
